import {
  format,
  formatDistanceToNow,
  fromUnixTime,
  differenceInCalendarDays,
  addMonths,
  startOfMonth,
  parseISO,
  parse,
} from 'date-fns';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';

import { clamp } from '/src/utils/math';
import { JSON_DATE_RANGE_ADAPTER } from '/src/utils/date-range-adapters';

export function formatDate(input) {
  return format(fromUnixTime(input), `MMM dd, yyyy`).replace(/\./g, '');
}

export function formatParseMinimalDate(input) {
  return format(parse(input, 'yyyy-MM-dd', new Date()), `MMMM d, yyyy`).replace(/\./g, '');
}

export function formatParseFullDateOfWeek(input) {
  return format(parse(input, 'yyyy-MM-dd', new Date()), `MMMM dd, yyyy - EEEE`).replace(/\./g, '');
}

export function formatShortDate(input) {
  const date = typeof input === 'number' ? fromUnixTime(input) : parse(input, 'yyyy-MM-dd', new Date());
  return format(date, `MMM dd`).replace(/\./g, '');
}

export function formatShortMinimalDate(input) {
  const date = typeof input === 'number' ? fromUnixTime(input) : parse(input, 'yyyy-MM-dd', new Date());
  return format(date, `MMMM d`).replace(/\./g, '');
}

export function utcStringToLocalTimeDate(input) {
  return format(utcToZonedTime(parseISO(input)), `MMM dd, y`).replace(/\./g, '');
}

export function formatParseDayOfWeek(input) {
  return format(parse(input, 'yyyy-MM-dd', new Date()), 'EEEE');
}

function getDateInServerLocalTime(input) {
  const serverTimeZone = 'Etc/UTC';
  const utcTime = zonedTimeToUtc(
    format(input, 'yyyy-MM-dd HH:mm'),
    Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone,
  );
  return utcToZonedTime(utcTime, serverTimeZone);
}

export function normalizeDate(input) {
  if (input instanceof Date) return input;
  if (typeof input === 'number') {
    // if it is seconds, convert from unix timestamp
    // if it is milliseconds, convert directly to Date
    return input < 1e12 ? fromUnixTime(input) : new Date(input);
  }
  // try parse whatever is provided
  return new Date(input);
}

export function formattedCurrentDate() {
  return new Date()
    .toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })
    .replaceAll('/', '-');
}

export function getDaysRemainingInMonthServerTime() {
  const serverTime = getDateInServerLocalTime(new Date());
  return clamp(
    differenceInCalendarDays(getDateInServerLocalTime(addMonths(startOfMonth(serverTime), 1)), serverTime),
    0,
    31,
  );
}

export function getDaysRemainingInMonthServerTimeFloatType() {
  const serverTime = getDateInServerLocalTime(new Date());
  const remainingDays = parseInt(getDaysRemainingInMonthServerTime());
  const serverHours = parseInt(format(serverTime, 'k'));

  if (serverHours >= 1 && serverHours < 12) {
    return remainingDays;
  } else if (serverHours >= 12 && serverHours < 24) {
    return remainingDays - 0.5;
  } else {
    return remainingDays - 1;
  }
}

function padNumber(value, padded = false) {
  if (!padded) return value;

  if (value >= 10) {
    return value;
  }
  return `0${value}`;
}

export function msToHHMMSS(ms, padded = false) {
  let seconds = Math.floor(ms / 1000);
  const hours = parseInt(seconds / 3600);
  seconds = seconds % 3600;
  const minutes = parseInt(seconds / 60);
  seconds = seconds % 60;

  if (hours > 0) {
    return `${padNumber(hours, padded)}:${padNumber(minutes, true)}:${padNumber(seconds, true)}`;
  }
  return `${padNumber(minutes, padded)}:${padNumber(seconds, true)}`;
}

export function msToHumanDuration(ms, compact = false) {
  let totalSeconds = ms < 1000 ? Math.round(ms / 10) / 100 : Math.floor(ms / 1000);

  let days = Math.floor(totalSeconds / 86400);
  totalSeconds -= days * 86400;

  let hours = Math.floor(totalSeconds / 3600);
  totalSeconds -= hours * 3600;

  let minutes = Math.floor(totalSeconds / 60);
  let seconds = totalSeconds - minutes * 60;

  if (ms < 1000) {
    seconds = Number(seconds.toFixed(2));
    if (seconds >= 60) {
      minutes += 1;
      seconds = 0;
    }
  } else {
    seconds = Math.floor(seconds);
  }

  if (minutes >= 60) {
    hours += Math.floor(minutes / 60);
    minutes = minutes % 60;
  }

  if (hours >= 24) {
    days += Math.floor(hours / 24);
    hours = hours % 24;
  }

  const formatSeconds = () => {
    if (ms >= 1000) {
      return `${seconds}s`;
    }
    const fixed = seconds % 1 === 0 ? seconds.toFixed(0) : seconds.toFixed(2);
    return `${fixed}s`;
  };

  let returnValue = '';
  if (days > 0) {
    returnValue += ` ${days}d`;
  }
  if (hours > 0) {
    returnValue += ` ${hours}h`;
  }
  if (minutes > 0) {
    returnValue += ` ${minutes}m`;
  }
  if (compact && seconds > 0) {
    returnValue += ` ${formatSeconds()}`;
  } else if (!compact) {
    returnValue += ` ${formatSeconds()}`;
  }

  if (returnValue === '') {
    returnValue += '0s';
  }

  return returnValue.trim();
}

export function formatDuration(input, { units = 'ms', padded = false } = {}) {
  if (!['ms', 's'].includes(units)) {
    throw new Error('formatDuration only accepts seconds (s) and milliseconds (ms)');
  }
  const date = new Date(units === 'ms' ? input : input * 1000);
  return format(date, padded ? 'mm:ss' : 'm:ss');
}

export function getInitialDate(queryParam) {
  const parsed = JSON_DATE_RANGE_ADAPTER.parse(queryParam);

  if (parsed.status !== 'valid') {
    return null;
  }

  return parsed.value;
}

export function dateDistance(date) {
  return formatDistanceToNow(fromUnixTime(date), { includeSeconds: true, addSuffix: true });
}
